import * as React from 'react';

import { DEFAULT_OPTION_CLASS_NAME } from './option-class-name';
import type { DropdownOption } from './dropdown-options';
import { getInjectedSelectComponents } from './select-components-injection';

type DropdownTextProps = {
  label: string;
  displayValue: string;
  optionClassName?: string;
};

/**
 * Read-only variant: renders the selected value as a static chip. Swallows
 * pointer/keyboard events so ProseMirror does not treat the block as editable.
 */
export const ReadOnlyLine: React.FC<DropdownTextProps> = ({
  label,
  displayValue,
  optionClassName = DEFAULT_OPTION_CLASS_NAME,
}) => (
  <div
    className="flex items-center gap-2 min-h-[1.5rem]"
    contentEditable={false}
    data-dropdown-block="true"
    onMouseDown={(e) => {
      e.stopPropagation();
    }}
    onClick={(e) => {
      e.stopPropagation();
    }}
    onKeyDown={(e) => {
      e.stopPropagation();
    }}
  >
    <span className="text-sm text-muted-foreground select-none">•</span>
    {label && <span className="text-sm text-muted-foreground">{label}:</span>}
    {displayValue && (
      <span
        className={`inline-flex h-6 items-center rounded-md border px-2 text-sm leading-5 ${optionClassName}`}
      >
        {displayValue}
      </span>
    )}
  </div>
);

/**
 * Placeholder shown for an editable dropdown before it is ready to mount the
 * real Select (see {@link useDropdownBlockState}), preventing BlockNote
 * position-resolution errors during the initial render.
 */
export const EditableFallback: React.FC<DropdownTextProps> = ({
  label,
  displayValue,
  optionClassName = DEFAULT_OPTION_CLASS_NAME,
}) => (
  <div
    className="flex items-center gap-2 min-h-[1.5rem]"
    contentEditable={false}
    data-dropdown-block="true"
  >
    <span className="text-sm text-muted-foreground select-none">•</span>
    <span className="text-sm text-muted-foreground">{label}:</span>
    <div
      className={`w-auto min-w-fit h-9 rounded-md border flex items-center justify-between px-3 text-sm ${optionClassName}`}
    >
      {displayValue ? (
        <span className="text-base font-medium leading-6">{displayValue}</span>
      ) : (
        <span>Select...</span>
      )}
    </div>
  </div>
);

type EditableDropdownProps = {
  label: string;
  selectedValue: string;
  displayValue: string;
  optionClassName: string;
  options: DropdownOption[];
  onValueChange: (newValue: string) => void;
};

/**
 * Editable variant: renders the injected Radix Select (or a static fallback
 * when Select components were never injected). Owns the outer wrapper that
 * stops ProseMirror from handling events on the block.
 */
export const EditableDropdown: React.FC<EditableDropdownProps> = ({
  label,
  selectedValue,
  displayValue,
  optionClassName,
  options,
  onValueChange,
}) => {
  const injectedSelectComponents = getInjectedSelectComponents();

  return (
    // Return JSX directly - don't wrap in component to avoid position resolution issues
    // Fixed vertical alignment: removed py-2 padding and use min-h-[1.5rem] to match BlockNote's default block height
    <div
      className="flex items-center gap-2 min-h-[1.5rem]"
      contentEditable={false}
      data-dropdown-block="true"
      onMouseDown={(e) => {
        // Prevent ProseMirror from handling mouse events on the dropdown
        e.stopPropagation();
      }}
      onClick={(e) => {
        // Prevent ProseMirror from handling click events on the dropdown
        e.stopPropagation();
      }}
      onKeyDown={(e) => {
        // Prevent ProseMirror from handling keyboard events
        e.stopPropagation();
      }}
    >
      <span className="text-sm text-muted-foreground select-none">•</span>
      {label && (
        <span className="text-sm text-muted-foreground">{label}:</span>
      )}
      {(() => {
        if (!injectedSelectComponents) {
          // Fallback if components not injected (should not happen in normal usage)
          return (
            <div
              className={`w-auto min-w-fit h-9 rounded-md border px-3 text-sm flex items-center ${optionClassName}`}
            >
              {displayValue ? (
                <span className="text-base font-medium leading-6">
                  {displayValue}
                </span>
              ) : (
                label
              )}
            </div>
          );
        }
        const {
          Select,
          SelectContent,
          SelectItem,
          SelectTrigger,
          SelectValue,
        } = injectedSelectComponents;
        return (
          <Select value={selectedValue} onValueChange={onValueChange}>
            <SelectTrigger
              className={`w-auto min-w-fit h-9 text-sm ${optionClassName}`}
              onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}
            >
              <SelectValue placeholder={label} />
            </SelectTrigger>
            <SelectContent
              onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}
            >
              {options.length === 0 ? (
                <SelectItem value="" disabled>
                  {label}
                </SelectItem>
              ) : (
                options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        );
      })()}
    </div>
  );
};
